import { FaApple } from '@react-icons/all-files/fa/FaApple';
import { FaLinux } from '@react-icons/all-files/fa/FaLinux';
import { FaWindows } from '@react-icons/all-files/fa/FaWindows';
import { HiMiniQuestionMarkCircle } from '@react-icons/all-files/hi2/HiMiniQuestionMarkCircle';
import { MfaStatus, UserCoverageSummary } from '@ampsec/platform-client';
import {
    IDENTITY_AND_ACCESS,
    TOOLING_COVERAGE_PROVIDER_ERROR_THRESHOLD_DAYS,
    TOOLING_COVERAGE_PROVIDER_WARNING_THRESHOLD_DAYS,
} from 'Core-utils/constants/constants';
import { getAssetDomains } from 'Core-utils/helpers/helpers';
import { OsType } from 'Core-utils/types';
import { IconWithStatus } from 'Components/molecules/IconWithStatus/IconWithStatus';
import { GridContext } from './AgGridCompliance.config';

export const getOsIcon = (osType: OsType, status: 'active' | 'warn' | 'error') => {
    const lowerCaseOsType = osType.toLowerCase();
    if (/windows/.test(lowerCaseOsType)) {
        return <IconWithStatus status={status} icon={<FaWindows size={20} className="text-structural-stroke-100" />} />;
    } else if (/mac/.test(lowerCaseOsType)) {
        return <IconWithStatus status={status} icon={<FaApple size={20} className="text-structural-stroke-100" />} />;
    } else if (/linux/.test(lowerCaseOsType)) {
        return <IconWithStatus status={status} icon={<FaLinux size={20} className="text-structural-stroke-100" />} />;
    } else {
        return <IconWithStatus status={status} icon={<HiMiniQuestionMarkCircle size={20} className="text-structural-stroke-100" />} />;
    }
};

export const getUserConnectorStatus = (
    isActive: boolean,
    lastActivity: string | undefined,
    options?: { considersLastActivity?: boolean },
) => {
    const considersLastActivity = options?.considersLastActivity ?? true;

    // Exception: some connectors ignore lastActivity; status follows isActive only.
    if (!considersLastActivity) {
        return isActive ? 'active' : 'warn';
    }

    if (!lastActivity) {
        return 'error';
    }

    if (!isActive) {
        return 'warn';
    }

    const diffInDays = (Date.now() - new Date(lastActivity).getTime()) / (1000 * 60 * 60 * 24);
    if (diffInDays > TOOLING_COVERAGE_PROVIDER_ERROR_THRESHOLD_DAYS) {
        return 'error';
    }
    if (diffInDays > TOOLING_COVERAGE_PROVIDER_WARNING_THRESHOLD_DAYS) {
        return 'warn';
    }
    return 'active';
};

export const getUserAssetStatus = (isActive: boolean, lastActivity: string | undefined) => {
    if (!lastActivity) {
        return 'error';
    }

    if (!isActive) {
        return 'warn';
    }

    const diffInDays = (Date.now() - new Date(lastActivity).getTime()) / (1000 * 60 * 60 * 24);
    if (diffInDays > TOOLING_COVERAGE_PROVIDER_ERROR_THRESHOLD_DAYS) {
        return 'error';
    }
    if (diffInDays > TOOLING_COVERAGE_PROVIDER_WARNING_THRESHOLD_DAYS) {
        return 'warn';
    }
    return 'active';
};

export const getUserConnectorsStatus = (
    userAccounts2: Record<string, { active: boolean; lastActivity?: string }[]>,
): string => {
    const userConnectors = Object.keys(userAccounts2 || {});
    if (userConnectors.length === 0) {
        return 'account missing';
    }
    for (const cid of userConnectors) {
        const accounts = userAccounts2[cid];
        if (Array.isArray(accounts) && accounts.length > 0) {
            return accounts.some((acc) => acc.active) ? 'active' : 'inactive';
        }
    }
    return 'account missing';
};

export const getEndpointsStatus = (
  assets: any[],
  domain: string,
  context: GridContext | undefined,
): string => {
  if (!Array.isArray(assets) || assets.length === 0 || !context) {
    return 'missing';
  }

  const providerNames = new Set<string>();
  const allConnectors = context.allConnectors ?? {};
  const providerNameIntegrationMap = context.providerNameIntegrationMap ?? {};
  const tenantSettings = context.tenantSettings;

  for (const asset of assets) {
    const assetAccounts2 = asset?.assetAccounts2 ?? {};
    for (const cid of Object.keys(assetAccounts2)) {
      const accounts = assetAccounts2[cid];
      if (!Array.isArray(accounts) || accounts.length === 0) {
        continue;
      }

      const connector = allConnectors[cid];
      const providerDisplayValue = connector?.provider?.displayValue;
      if (!providerDisplayValue) {
        continue;
      }

      const contentfulData = providerNameIntegrationMap?.[providerDisplayValue];
      const assetDomains = tenantSettings
        ? getAssetDomains(connector.id, providerDisplayValue, tenantSettings)
        : [];
      const domains = assetDomains.length > 0
        ? assetDomains
        : contentfulData?.domains?.map((d: any) => d.fields.name) ?? [];

      if (Array.isArray(domains) && domains.includes(domain)) {
        providerNames.add(contentfulData?.name ?? providerDisplayValue);
      }
    }
  }

  if (providerNames.size === 0) {
    return 'missing';
  }

  return Array.from(providerNames).sort((a, b) => a.localeCompare(b)).join(', ');
};

export const getUserRiskReason = (
    row: UserCoverageSummary | undefined,
    context: GridContext | undefined,
): string => {
    if (!context || !row) {
        return '';
    }

    const allConnectors = context.allConnectors ?? {};
    const providerNameIntegrationMap = context.providerNameIntegrationMap ?? {};
    const tenantSettings = context.tenantSettings;
    const isLoading = context.isLoading;

    // Resolve the identity-domain providers for this user, mirroring DomainCellRenderer's
    // user-cell resolution (userAccounts2 + providerNameIntegrationMap/tenantSettings). The grid
    // only renders a provider when it has at least one account (provider.count > 0), so keep only
    // the providers where the user actually has an account — per-provider risk is evaluated there.
    const identityProviders = Object.keys(row.userAccounts2 ?? {})
        .map((cid) => {
            const connector = allConnectors[cid];
            const providerDisplayValue = connector?.provider?.displayValue;
            const contentfulData = providerNameIntegrationMap?.[providerDisplayValue];
            const assetDomains = tenantSettings && providerDisplayValue
                ? getAssetDomains(cid, providerDisplayValue, tenantSettings)
                : [];
            const domains = !isLoading && assetDomains.length === 0
                ? contentfulData?.domains?.map((d: any) => d.fields.name)
                : assetDomains;
            return { cid, name: contentfulData?.name, domains };
        })
        .filter((p) => Array.isArray(p.domains) && p.domains.includes(IDENTITY_AND_ACCESS))
        .filter((p) => (row.userAccounts2?.[p.cid]?.length ?? 0) > 0);

    // Account/tool Missing: only assert when we can confirm there is no identity tool. While the
    // context is still loading the connector/provider maps are unpopulated, so an empty
    // resolution would wrongly flag every user — in that state we cannot confirm and report none.
    if (identityProviders.length === 0) {
        return isLoading ? '' : 'Account/tool Missing';
    }

    const reasons: string[] = [];

    // Last Logged In: reuse the grid's own connector-status logic (an inactive account, or
    // activity past the warn/error thresholds) so the reason matches what the grid flags as a risk.
    const hasActivityRisk = identityProviders.some((provider) =>
        (row.userAccounts2?.[provider.cid] ?? []).some((account) => {
            const lastActivity = (account as any)?.lastActivity as string | undefined;
            const considersLastActivity = provider.name?.toLowerCase() !== 'amplifier';
            return getUserConnectorStatus(account?.active ?? false, lastActivity, {
                considersLastActivity,
            }) !== 'active';
        }),
    );
    if (hasActivityRisk) {
        reasons.push('Last Logged In');
    }

    // MFA status per identity provider (falling back to the row summary, then DISABLED, matching
    // DomainCellRenderer's default when a provider has no report).
    const mfaStatuses = identityProviders.map((provider) =>
        row.mfaReports?.[provider.cid]?.mfaStatus ?? row.mfaSummary?.mfaStatus ?? MfaStatus.DISABLED,
    );
    if (mfaStatuses.some((s) => s === MfaStatus.DISABLED || s === MfaStatus.NOT_ENROLLED)) {
        reasons.push('Missing MFA');
    }
    if (mfaStatuses.some((s) => s === MfaStatus.ENABLED_INSECURE)) {
        reasons.push('Weak MFA Factors');
    }

    return reasons.join('; ');
};
